import React from "react";
import { Text, Pill, Icon } from "components/commons";
import { PillType } from "enums";
import lang from "translations";

const LINE_COLOR = "#7FC9DB";
const NODE_WIDTH = 130;
const GAP_WIDTH = 80;
const BADGE_SIZE = 40;

// State names are free-form per venue (confirmed -- no enum/constant exists anywhere in
// the backend), so this is a best-effort keyword match for the common cases, with a
// neutral fallback for anything venue-specific it doesn't recognize.
const stateIconMeta = (state) => {
  const s = (state || "").toUpperCase();
  if (/(SERVED|COMPLETE|PAID|DONE|CLOSED|SETTLED|AVAILABLE)/.test(s)) {
    return { icon: "check-only", bg: "#E4F3F7", color: "#1C4F5A" };
  }
  if (/(READY|CONFIRM|ACCEPT)/.test(s)) {
    return { icon: "notification-bell", bg: "#E3F6EA", color: "#1F9254" };
  }
  if (/(COOK|PREPAR|PROCESS|PROGRESS|OCCUPIED)/.test(s)) {
    return { icon: "sync", bg: "#FDF3E3", color: "#B8860B" };
  }
  if (/(VOID|CANCEL|FAIL|REJECT|DIRTY)/.test(s)) {
    return { icon: "cancel", bg: "#FBE7E7", color: "#C0392B" };
  }
  return { icon: "guest", bg: "#EEF2F5", color: "#5C6B73" };
};

// Horizontal timeline via CSS grid (not flexbox) so the connecting line and every badge
// share the same row and stay perfectly aligned regardless of label/timestamp length.
// Inline styles throughout -- this project's Tailwind build runs classic (non-JIT), so
// arbitrary grid-template-columns values wouldn't generate any CSS.
//
// entries[i] is the transition INTO its toState -- there's no real state before the
// first entry, so the timeline starts at entries[0].toState rather than showing a
// synthetic "no prior state" node with a meaningless leading duration.
const Timeline = ({ entries }) => {
  if (!entries?.length) {
    return <Text color="text-gray-400">{lang.noTimelineYet}</Text>;
  }

  const nodes = entries.map((entry) => ({ label: entry.toState, timestamp: entry.changedAt }));
  const segments = entries.slice(1).map((entry) => entry.durationLabel);

  const gridTemplateColumns = nodes
    .map((_, i) => (i < nodes.length - 1 ? `${NODE_WIDTH}px ${GAP_WIDTH}px` : `${NODE_WIDTH}px`))
    .join(" ");

  return (
    <div className="bg-gray-lightest rounded p-md" style={{ overflowX: "auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns,
          gridTemplateRows: "auto auto auto",
          rowGap: 8,
          paddingBottom: 4,
        }}
      >
        {nodes.map((node, index) => {
          const column = index * 2 + 1;
          const isLast = index === nodes.length - 1;
          const meta = stateIconMeta(node.label);
          return (
            <React.Fragment key={index}>
              <div
                style={{
                  gridColumn: column,
                  gridRow: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: BADGE_SIZE,
                    height: BADGE_SIZE,
                    borderRadius: "50%",
                    backgroundColor: meta.bg,
                    border: "3px solid #fff",
                    boxShadow: `0 0 0 1px ${LINE_COLOR}`,
                  }}
                >
                  <span style={{ color: meta.color, display: "flex" }}>
                    <Icon name={meta.icon} fontSize="16px" paddingless />
                  </span>
                </span>
              </div>
              <div style={{ gridColumn: column, gridRow: 2, textAlign: "center", padding: "0 4px" }}>
                <Text size="text-sm" strong>{node.label}</Text>
              </div>
              <div style={{ gridColumn: column, gridRow: 3, textAlign: "center", padding: "0 4px" }}>
                {node.timestamp && <Text size="text-xs" color="text-gray">{node.timestamp}</Text>}
              </div>
              {!isLast && (
                <div
                  style={{
                    gridColumn: column + 1,
                    gridRow: 1,
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      height: 3,
                      borderRadius: 2,
                      backgroundColor: LINE_COLOR,
                    }}
                  />
                  <div style={{ position: "relative" }}>
                    <Pill size="text-xs" type={PillType.Blueish}>
                      {segments[index]}
                    </Pill>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default Timeline;
